import { useQuery } from "@apollo/client";
import {
  FeatureFlagKey,
  FeatureFlagMetadata,
  FeatureFlagType,
  isFeatureFlagKey,
} from "encamp-shared/src/featureFlags";
import { useMemo } from "react";

import { gql } from "../generated-graphql";
import { FeatureFlagType as FeatureFlagTypeGql } from "../generated-graphql/graphql";

/**
 * Staff-only feature flag metadata, read straight from Apollo's cache via the
 * staff `featureFlags` query — no client-side store or provider. The customer
 * app never needs this (it gates on the server-resolved `Tenant.featureFlags`);
 * only staff screens (toggle panels, chips, filter columns, the flags admin)
 * render flag labels/types/dependencies, and staff already load this query.
 *
 * Selects only the cheap metadata scalars so it doesn't trigger the adoption /
 * subscription field resolvers on `FeatureFlag`.
 */
export const FEATURE_FLAG_META_QUERY = gql(`
  query FeatureFlagMetaList {
    featureFlags {
      key
      label
      description
      type
      parentKey
      defaultValue
      staffOverride
      inDevelopment
      requires
    }
  }
`);

const GQL_TYPE_TO_META: Record<FeatureFlagTypeGql, FeatureFlagType> = {
  [FeatureFlagTypeGql.Module]: FeatureFlagType.Module,
  [FeatureFlagTypeGql.AddOn]: FeatureFlagType.AddOn,
  [FeatureFlagTypeGql.Other]: FeatureFlagType.Other,
};

/** Safe metadata for a key with no loaded row (still loading / newly-added flag). */
export function stubFlagMeta(key: FeatureFlagKey): FeatureFlagMetadata {
  return {
    label: key,
    description: "",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  };
}

/** Synchronous metadata accessor for a key — returns a safe stub if not loaded. */
export type GetFlagMeta = (key: FeatureFlagKey) => FeatureFlagMetadata;

export type UseFeatureFlagMetaResult = {
  getMeta: GetFlagMeta;
  metaByKey: Map<FeatureFlagKey, FeatureFlagMetadata>;
  loading: boolean;
};

export function useFeatureFlagMeta(): UseFeatureFlagMetaResult {
  const { data, loading } = useQuery(FEATURE_FLAG_META_QUERY, { fetchPolicy: "cache-first" });

  return useMemo(() => {
    const metaByKey = new Map<FeatureFlagKey, FeatureFlagMetadata>();
    for (const row of data?.featureFlags ?? []) {
      if (!isFeatureFlagKey(row.key)) continue;
      metaByKey.set(row.key, {
        label: row.label,
        description: row.description,
        type: GQL_TYPE_TO_META[row.type],
        parentKey: row.parentKey && isFeatureFlagKey(row.parentKey) ? row.parentKey : null,
        defaultValue: row.defaultValue,
        staffOverride: row.staffOverride,
        inDevelopment: row.inDevelopment,
        requires: row.requires.filter(isFeatureFlagKey),
      });
    }
    const getMeta: GetFlagMeta = (key) => metaByKey.get(key) ?? stubFlagMeta(key);
    return { getMeta, metaByKey, loading };
  }, [data, loading]);
}
